/**
 * ConsentLivePreview Component
 * 
 * Shows a LIVE preview of the consent narrative text in edit mode.
 * Fetches from the backend API to ensure the provider sees the EXACT text
 * that will be captured in the PDF when signed.
 * 
 * This is the "Pre-Sign Verification" - what you see is what you get.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Users, Monitor, Phone, Eye, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { fetchConsentNarrative, getModalityDisplayInfo } from '../../utils/consentHelpers';

/**
 * ConsentLivePreview - Edit mode component
 * 
 * @param {Object} props
 * @param {Object} props.consentData - Current consent form values (pos_code, modality, provider_location)
 * @param {string} props.token - Auth token for API calls
 */
export function ConsentLivePreview({ consentData, token }) {
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  // Fetch live preview when consent data changes
  useEffect(() => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Don't fetch if no meaningful data
    if (!consentData?.pos_code || !consentData?.modality) {
      setNarrative(null);
      return;
    }

    // Debounce API calls (300ms)
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await fetchConsentNarrative(consentData, token);
        setNarrative(result);
      } catch (err) {
        console.error('Error fetching consent preview:', err);
        setError('Unable to load preview');
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [consentData?.pos_code, consentData?.modality, consentData?.provider_location, token]);

  // Show nothing if no consent data
  if (!consentData?.pos_code || !consentData?.modality) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center gap-2 text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading preview...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="border-2 border-dashed border-red-200 rounded-lg p-4 bg-red-50">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  // No valid narrative
  if (!narrative?.is_valid) {
    return null;
  }

  const displayInfo = getModalityDisplayInfo(narrative.modality);
  const IconComponent = narrative.modality === 'in_person' ? Users 
    : narrative.modality === 'audio_visual' ? Monitor 
    : Phone;

  return (
    <div className="relative" data-testid="consent-live-preview">
      {/* Live Preview Badge */}
      <div className="absolute -top-2 left-3 z-10">
        <Badge className="bg-amber-100 text-amber-800 text-xs flex items-center gap-1 shadow-sm">
          <Eye className="h-3 w-3" />
          Live Preview
        </Badge>
      </div>
      
      <div className={`border-2 rounded-lg p-4 pt-5 ${displayInfo.bgColor} ${displayInfo.borderColor}`}>
        <div className="flex items-start gap-2">
          <IconComponent className={`h-5 w-5 ${displayInfo.iconColor} mt-0.5`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-gray-900">Consent Affirmation</span>
              <Badge className="bg-green-200 text-green-800 text-xs">✓ Will be confirmed</Badge>
            </div>
            <p className="text-sm text-gray-700">{narrative.text}</p>
            <p className="text-xs text-gray-400 mt-2 italic">
              This is the exact text that will appear in the PDF when signed (v{narrative.version})
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsentLivePreview;
